// frontend/src/pages/StatusConfirmationPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
    Box, 
    CircularProgress, 
    Typography, 
    Container, 
    Button,
    Paper,
    Alert,
    Link
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

function StatusConfirmationPage() {
    const { token } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [bidDetails, setBidDetails] = useState(null);
    const [stage, setStage] = useState(null);
    const [stageLabel, setStageLabel] = useState(null);
    
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const stageParam = searchParams.get('stage');
        
        if (!stageParam) {
            console.log('[StatusConfirmationPage] Missing stage parameter');
            setError('Invalid confirmation link - missing status information');
            setLoading(false);
            return;
        }

        setStage(stageParam);

        // Fetch bid details for preview
        console.log('[StatusConfirmationPage] Fetching preview data', {
            token: token?.substring(0, 8) + '...',
            stage: stageParam,
            timestamp: new Date().toISOString()
        });

        fetch(`${process.env.REACT_APP_API_URL}/api/feedback/preview-status/${token}?stage=${stageParam}`)
            .then(response => {
                console.log('[StatusConfirmationPage] Preview response received', {
                    status: response.status,
                    timestamp: new Date().toISOString()
                });
                
                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('This feedback link has expired or is invalid.');
                    }
                    throw new Error('Failed to load confirmation details.');
                }
                return response.json();
            })
            .then(data => {
                console.log('[StatusConfirmationPage] Preview data loaded', {
                    bidDetails: data.bidDetails,
                    stageLabel: data.stageLabel
                });
                setBidDetails(data.bidDetails);
                setStageLabel(data.stageLabel);
                setLoading(false);
            })
            .catch(err => {
                console.error('[StatusConfirmationPage] Error loading preview:', err);
                setError(err.message || 'There was a problem loading the confirmation page.');
                setLoading(false);
            });
    }, [token, location.search]);

    const handleConfirm = async () => {
        console.log('[StatusConfirmationPage] Confirm button clicked', {
            stage,
            token: token?.substring(0, 8) + '...',
            timestamp: new Date().toISOString()
        });

        setSubmitting(true);
        setError(null);

        try {
            const formData = new URLSearchParams();
            formData.append('stage', stage);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/feedback/update-stage/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData.toString()
            });

            console.log('[StatusConfirmationPage] POST response received', {
                status: response.status,
                redirected: response.redirected,
                url: response.url,
                timestamp: new Date().toISOString()
            });
            
            if (response.redirected) {
                console.log('[StatusConfirmationPage] Following redirect to:', response.url);
                window.location.href = response.url;
            } else if (response.ok) {
                // If no redirect, navigate manually to feedback form
                window.location.href = `/feedback/${token}`;
            } else {
                throw new Error('Failed to process confirmation');
            }
        } catch (err) {
            console.error('[StatusConfirmationPage] Error during confirmation:', err);
            setError('There was a problem confirming your response. Please try again.');
            setSubmitting(false);
        }
    };

    const handleViewFullForm = () => {
        console.log('[StatusConfirmationPage] View full form clicked');
        window.location.href = `/feedback/${token}`;
    };

    // Format currency for display
    const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };
    
    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Box textAlign="center">
                    <CircularProgress size={60} />
                    <Typography variant="h5" sx={{ mt: 3 }}>
                        Loading confirmation...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Please wait while we verify your link.
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box textAlign="center">
                        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h5" color="error" gutterBottom>
                            {error}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
                            Please contact support if this problem persists.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => window.location.href = '/'}
                        >
                            Return to Home
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }
    
    return (
        <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                {/* Header */}
                <Box textAlign="center" mb={4}>
                    <CheckCircleOutlineIcon 
                        color="primary" 
                        sx={{ fontSize: 60, mb: 2 }} 
                    />
                    <Typography variant="h4" gutterBottom fontWeight="bold">
                        Confirm: {stageLabel}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Please review the bid details below and confirm your selection.
                    </Typography>
                </Box>

                {/* Bid Details Box */}
                <Box 
                    sx={{ 
                        backgroundColor: '#f8f9fa',
                        padding: 3,
                        borderRadius: 2,
                        mb: 4,
                        border: '1px solid #dee2e6'
                    }}
                >
                    <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                        Bid Details
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Project:</strong> {bidDetails?.projectName}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Package:</strong> {bidDetails?.bidPackageName}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>Submitted Bid:</strong> {formatCurrency(bidDetails?.submittedBidAmount)}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Subcontractor:</strong> {bidDetails?.submittingCompanyName}
                        </Typography>
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        justifyContent: 'center',
                        mb: 3
                    }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleConfirm}
                        disabled={submitting}
                        sx={{ 
                            minWidth: 200,
                            py: 1.5,
                            fontSize: '1.1rem'
                        }}
                    >
                        {submitting ? 'Confirming...' : 'Confirm'}
                    </Button>
                    
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={handleViewFullForm}
                        disabled={submitting}
                        sx={{ 
                            minWidth: 200,
                            py: 1.5,
                            fontSize: '1.1rem'
                        }}
                    >
                        View Full Form Instead
                    </Button>
                </Box>

                {/* Info text */}
                <Alert severity="info" sx={{ mt: 3 }}>
                    After confirming, you'll be taken to the full feedback form where you can provide 
                    additional details if needed.
                </Alert>
            </Paper>
        </Container>
    );
}

export default StatusConfirmationPage;